import { el } from './dom.js';

export type ProductMode = 'spreadsheet' | 'document' | 'slides';

export interface ProductModePicker {
  element: HTMLElement;
  open(): void;
  close(): void;
  onSelect(handler: (mode: ProductMode) => void): void;
}

/** Shared mode picker for NumixSheet's spreadsheet, document, and slide workspaces. */
export function createProductModePicker(current: ProductMode = 'spreadsheet'): ProductModePicker {
  let selectHandler: ((mode: ProductMode) => void) | null = null;
  const backdrop = el('div', { className: 'nx-mode-backdrop', attrs: { hidden: 'hidden' } });
  const dialog = el('section', { className: 'nx-mode-dialog', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'nx-mode-title' } });
  const close = el('button', { className: 'nx-mode-close', text: '×', attrs: { type: 'button', 'aria-label': 'Close workspace picker' } });
  const cards = [
    { mode: 'spreadsheet' as const, icon: '▦', title: 'Spreadsheet', body: 'Large tables, formulas, filters, imports and DuckDB analysis.' },
    { mode: 'document' as const, icon: '▤', title: 'Document', body: 'Write briefs, reports and notes with structured blocks and tables.' },
    { mode: 'slides' as const, icon: '▥', title: 'Presentation', body: 'Build visual slides with layouts, charts and speaker notes.' },
  ];
  const cardNodes = cards.map((item) => {
    const button = el('button', { className: `nx-mode-card ${item.mode === current ? 'is-current' : ''}`, attrs: { type: 'button', 'data-mode': item.mode } }, [
      el('span', { className: 'nx-mode-icon', text: item.icon }),
      el('span', { className: 'nx-mode-card-copy' }, [
        el('strong', { text: item.title }),
        el('small', { text: item.body }),
      ]),
      el('span', { className: 'nx-mode-arrow', text: '→' }),
    ]);
    button.addEventListener('click', () => {
      cardNodes.forEach((node) => node.classList.toggle('is-current', node === button));
      selectHandler?.(item.mode);
      backdrop.hidden = true;
    });
    return button;
  });
  dialog.append(
    el('div', { className: 'nx-mode-heading' }, [
      el('div', { className: 'nx-mode-kicker', text: 'NUMIX WORKSPACE' }),
      el('h2', { attrs: { id: 'nx-mode-title' }, text: 'Choose how you want to work' }),
      el('p', { text: 'One workspace for data, writing and presentations.' }),
    ]),
    close,
    el('div', { className: 'nx-mode-cards' }, cardNodes),
  );
  backdrop.appendChild(dialog);
  close.addEventListener('click', () => { backdrop.hidden = true; });
  backdrop.addEventListener('click', (event) => { if (event.target === backdrop) backdrop.hidden = true; });
  backdrop.addEventListener('keydown', (event) => { if (event.key === 'Escape') backdrop.hidden = true; });
  return {
    element: backdrop,
    open: () => { backdrop.hidden = false; },
    close: () => { backdrop.hidden = true; },
    onSelect: (handler) => { selectHandler = handler; },
  };
}
